<template>
  <!--
    M2-A — card tabelado em 4 colunas. A cor (card e chip) vem SÓ de `sla.state` do
    servidor (§6.3); o relógio da tela só reescreve o texto relativo.
  -->
  <article
    class="mb-4 rounded-lg border p-4"
    :class="CARD_CLASS[tone]"
    data-testid="activity-request-card"
    :data-sla-state="item.sla?.state ?? 'UNKNOWN'"
  >
    <header class="flex flex-wrap items-center gap-2">
      <span class="text-xs font-bold text-[#52526F]">#{{ item.entityId }}</span>
      <h4 class="text-md font-bold text-textPrimary">Solicitação de Registro de Atividade</h4>
      <span class="rounded-full bg-lightGray30 px-2 py-0.5 text-[10px] font-bold text-neutral50">
        Aguardando envio
      </span>
      <span
        v-for="badge in item.badges"
        :key="badge.key"
        class="rounded-full px-2 py-0.5 text-[11px]"
        :class="[
          QUEUE_TONE_PILL_CLASS[badge.tone],
          badge.tone === 'crit' ? 'font-bold ring-1 ring-red-300' : ''
        ]"
        :data-testid="`activity-request-badge-${badge.key}`"
      >
        {{ badge.label }}
      </span>
      <span
        class="ml-auto rounded-md px-2.5 py-1 text-xs font-bold"
        :class="CHIP_CLASS[tone]"
        data-testid="activity-request-deadline-chip"
      >
        {{ deadlineChipText }}
      </span>
    </header>

    <p
      v-if="payload.reopen"
      class="mt-2 rounded-md border border-alert bg-[#FDF1DF] px-3 py-2 text-xs text-textPrimary"
      data-testid="activity-request-reopen"
    >
      {{ reopenText }}
      <template v-if="reopenReason"> <br />Motivo: “{{ reopenReason }}” </template>
    </p>

    <div class="mt-3 grid grid-cols-1 gap-4 text-sm md:grid-cols-4">
      <section data-testid="activity-request-col-station">
        <h5 class="mb-1 text-xs font-bold uppercase text-neutral50">Plantão</h5>
        <p class="m-0">{{ period.weekday ? `${period.weekday}, ` : '' }}{{ period.date }}</p>
        <p class="m-0">Previsto {{ period.range }} · {{ period.duration }}</p>
        <p v-if="shiftName" class="m-0 text-neutral50">Turno {{ shiftName }}</p>
        <p class="m-0 text-neutral50">{{ item.location.healthUnitName }}</p>
        <p class="m-0 text-neutral50">{{ item.location.scheduleName }}</p>
      </section>

      <section data-testid="activity-request-col-doctor">
        <h5 class="mb-1 text-xs font-bold uppercase text-neutral50">Médico</h5>
        <p class="m-0 font-bold">{{ item.actor?.name ?? 'Médico não informado' }}</p>
        <p class="m-0 text-neutral50">{{ item.actor?.crm ?? 'CRM não informado' }}</p>
        <span
          v-if="item.actor"
          class="mt-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-bold"
          :class="item.actor.ourTeam ? QUEUE_TONE_PILL_CLASS.ok : QUEUE_TONE_PILL_CLASS.warn"
          data-testid="activity-request-team-badge"
        >
          {{ item.actor.ourTeam ? 'Do time' : 'Fora do time' }}
        </span>
      </section>

      <section data-testid="activity-request-col-expected">
        <h5 class="mb-1 text-xs font-bold uppercase text-neutral50">Registro esperado</h5>
        <p class="m-0">
          Perna <b>{{ legLabel }}</b>
        </p>
        <p class="m-0">
          Modalidade <b>{{ modeLabel }}</b>
        </p>
        <p class="m-0">Regra {{ ruleText }}</p>
        <p class="m-0">
          Prazo <b>{{ deadlineAbsolute ?? 'sem prazo' }}</b>
          <span class="block text-[11px] text-neutral50">{{ zoneLabel }}</span>
        </p>
      </section>

      <section data-testid="activity-request-col-situation">
        <h5 class="mb-1 text-xs font-bold uppercase text-neutral50">Situação</h5>
        <p
          v-for="leg in legRows"
          :key="leg.key"
          class="m-0 mb-1 rounded px-2 py-0.5"
          :class="
            leg.missing ? 'border border-dashed border-negative text-negative' : 'text-textPrimary'
          "
          :data-testid="`activity-request-leg-${leg.key}`"
          :data-missing="String(leg.missing)"
        >
          {{ leg.text }}
        </p>
        <p
          v-if="payload.payment.hasAntecipe || payload.payment.hasInCash"
          class="m-0 text-xs text-neutral50"
        >
          Pagamento prioritário retido até o registro de: {{ legLabel }}.
        </p>
        <p v-if="item.value" class="m-0 text-xs" data-testid="activity-request-value">
          Valor a pagar (Financeiro) <b>{{ formatBRL(item.value.amount) }}</b>
        </p>
      </section>
    </div>

    <footer class="mt-3 flex flex-wrap items-center gap-2 border-t border-lightGray30 pt-2">
      <p class="m-0 text-xs text-neutral50" data-testid="activity-request-footer">
        Aberta em {{ openedAtText }} ({{ originText }}) · cancela sozinha se o que falta ({{
          legLabel
        }}) for coberto por folha aprovada, check tardio, App vizinho ou permanência.
      </p>
      <div class="ml-auto flex gap-2">
        <div data-testid="activity-request-details-button">
          <OutlinedButton
            text="Ver detalhes"
            width="130px"
            height="32px"
            @click="emit('details', item.entityId)"
          />
        </div>
        <div data-testid="activity-request-send-record-button">
          <OutlinedButton
            text="Enviar registro"
            width="150px"
            height="32px"
            @click="emit('sendRecord', item.entityId)"
          />
        </div>
      </div>
    </footer>
  </article>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import { QUEUE_TONE_PILL_CLASS } from '@/components/designSystem/QueueViews/types'
import type { QueueTone } from '@/components/designSystem/QueueViews/types'
import {
  EFFECTIVE_SOURCE_KIND_LABELS,
  REQUESTED_LEG_LABELS
} from '@/constants/ds-attendance.constants'
import type { EffectiveSourceKind } from '@/constants/ds-attendance.constants'
import type { ActivityRequestItem } from '@/types/activity-requests.types'
import { DEFAULT_UTC_OFFSET, formatDayMonthInOffset, formatHourMinuteInOffset } from '@/utils/date'

import {
  formatBRL,
  formatDateTimeInStation,
  formatDeadlineRelative,
  formatLegDeviation,
  formatStationPeriod,
  openingRuleLabel,
  registrationModeLabel,
  sendWindowLabel,
  slaTone,
  timezoneLabel
} from '../../utils/activity-request-format'

const props = defineProps<{
  item: ActivityRequestItem
  /** Relógio corrigido pelo servidor (`useServerNowTick`); `null` antes da 1ª resposta. */
  nowMs: number | null
}>()

const emit = defineEmits<{
  details: [entityId: number]
  sendRecord: [entityId: number]
}>()

const CARD_CLASS: Record<QueueTone, string> = {
  crit: 'border-negative bg-red-50',
  warn: 'border-alert bg-orange-50',
  ok: 'border-lightGray30 bg-white',
  info: 'border-lightGray30 bg-white',
  neutral: 'border-lightGray30 bg-white'
}

const CHIP_CLASS: Record<QueueTone, string> = {
  crit: 'bg-red-600 text-white',
  warn: 'bg-orange-500 text-white',
  ok: 'bg-green-600 text-white',
  info: 'bg-blue-600 text-white',
  neutral: 'bg-lightGray30 text-neutral50'
}

const STATE_FALLBACK: Record<string, string> = {
  OVERDUE: 'Vencida',
  ATTENTION: 'Vence em 24 h',
  URGENT: 'Vence em 24 h',
  ON_TIME: 'No prazo'
}

const payload = computed(() => props.item.payload)
const offset = computed(
  () => props.item.timezone || payload.value.station.timezone || DEFAULT_UTC_OFFSET
)
const tone = computed(() => slaTone(props.item.sla?.state))

const deadlineIso = computed(
  () => payload.value.sendDeadlineAt ?? props.item.sla?.deadlineAt ?? null
)

const deadlineChipText = computed(
  () =>
    formatDeadlineRelative(deadlineIso.value, props.nowMs) ??
    STATE_FALLBACK[props.item.sla?.state ?? ''] ??
    'Sem prazo'
)

const deadlineAbsolute = computed(() => formatDateTimeInStation(deadlineIso.value, offset.value))
const zoneLabel = computed(() => timezoneLabel(offset.value))

const period = computed(() =>
  formatStationPeriod(payload.value.station.startAt, payload.value.station.endAt, offset.value)
)
const shiftName = computed(() => props.item.location.shiftName ?? payload.value.station.shiftName)

const legLabel = computed(
  () => REQUESTED_LEG_LABELS[payload.value.requestedLeg] ?? payload.value.requestedLeg
)

const modeLabel = computed(() =>
  registrationModeLabel(payload.value.registrationMode ?? payload.value.requestChannel)
)

const ruleText = computed(() => {
  const window = sendWindowLabel(payload.value.sendWindowHours)
  const rule = openingRuleLabel(payload.value.openingRule)
  return window ? `${rule} · janela de ${window}` : rule
})

const sourceLabel = (source: string | null): string =>
  source
    ? (EFFECTIVE_SOURCE_KIND_LABELS[source as EffectiveSourceKind] ?? source)
    : 'fonte não informada'

const legRows = computed(() =>
  (
    [
      { key: 'checkin', label: 'Entrada', expected: payload.value.station.startAt },
      { key: 'checkout', label: 'Saída', expected: payload.value.station.endAt }
    ] as const
  ).map((leg) => {
    const effective = payload.value.effectiveSummary?.[leg.key] ?? null
    const expectedTime = leg.expected
      ? (formatHourMinuteInOffset(leg.expected, offset.value) ?? '--:--')
      : '--:--'
    if (!effective?.value) {
      return { key: leg.key, missing: true, text: `${leg.label} pendente · prev. ${expectedTime}` }
    }
    const time = formatHourMinuteInOffset(effective.value, offset.value) ?? '--:--'
    const deviation = formatLegDeviation(effective.value, leg.expected)
    return {
      key: leg.key,
      missing: false,
      text: `${leg.label} ✓ ${time} · prev. ${expectedTime}${deviation ? ` · ${deviation}` : ''} · ${sourceLabel(effective.source)}`
    }
  })
)

/** A justificativa de quem reprovou é o texto útil; o rótulo do código é o fallback. */
const reopenReason = computed(() => {
  const reopen = payload.value.reopen
  return (
    reopen?.rejectionJustification ??
    reopen?.rejectionReasonLabel ??
    reopen?.rejectionReason ??
    null
  )
})

const reopenText = computed(() => {
  const reopen = payload.value.reopen
  if (!reopen) return ''
  const submitted = reopen.previousSubmittedByName
    ? ` (enviado por ${reopen.previousSubmittedByName}${
        reopen.previousSubmittedAt
          ? ` em ${formatDayMonthInOffset(reopen.previousSubmittedAt, offset.value) ?? ''}`
          : ''
      })`
    : ''
  const rejected = reopen.rejectedByName
    ? ` · reprovado por ${reopen.rejectedByName}${
        reopen.rejectedAt
          ? ` em ${formatDayMonthInOffset(reopen.rejectedAt, offset.value) ?? ''}`
          : ''
      }`
    : ''
  return `Reaberta após rejeição do #${reopen.previousContainerId}${submitted}${rejected}`
})

const openedAtText = computed(
  () =>
    formatDateTimeInStation(payload.value.openedAt ?? props.item.occurredAt, offset.value) ??
    'data não informada'
)
const originText = computed(() =>
  payload.value.openingRule === 'REJECTION_REOPEN' ? 'reaberta · rejeição' : 'automática · política'
)
</script>
